import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from './Input';
import { currencies } from '../../../utils/functions';
import {
  caretAfterDigits,
  digitsBefore,
  groupMoney,
  moneyFormatSpec,
  parseMoney,
} from '../../../utils/moneyFormat';

/**
 * Reusable money-amount input (tag matter-economics). Shows the symbol for the
 * given currency as a prefix. Reused by the matter pricing fields (budget and
 * fixed fee), rate overrides, org and user billing rates, and outcomes.
 *
 * THOUSANDS GROUPING. The field DISPLAYS `600,000` and EMITS `600000`. The
 * separators and the number of decimals come from the locale and the currency
 * (see `utils/moneyFormat`), so a German user sees `600.000,50` and a JPY
 * amount takes no decimals at all.
 *
 * The value contract is unchanged: `value` in and `onChange` out are both
 * plain numeric strings that survive `Number()`. Callers keep parsing on
 * submit exactly as before - nothing downstream ever sees a separator.
 *
 * This is why the input is `type="text"`. A number input silently refuses any
 * value containing a separator, so grouping and `type="number"` cannot
 * coexist; `inputMode="decimal"` keeps the numeric keypad on mobile.
 */
interface CurrencyInputProps {
  /** Plain numeric string - no separators. Parse on submit as before. */
  value: string;
  /** Receives a plain numeric string, never the grouped display text. */
  onChange: (value: string) => void;
  /** ISO 4217 code used for the symbol and the decimal precision, e.g. "EUR". */
  currency?: string;
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/** Look up a currency's display symbol, falling back to the code itself. */
export const currencySymbol = (code?: string): string => {
  if (!code) return '';
  const upper = code.toUpperCase();
  return currencies.find((c) => c.value === upper)?.symbol || code;
};

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  currency,
  id,
  name,
  label,
  placeholder,
  error,
  required,
  disabled,
  className,
}) => {
  const { i18n } = useTranslation();
  // Optional chaining: a caller (or a test) may supply an i18n context without
  // an `i18n` object, and a money input is not a reason to fail to render.
  const spec = moneyFormatSpec(i18n?.language || 'en', currency);

  const inputRef = useRef<HTMLInputElement>(null);
  /*
    The grouped text is local state rather than derived on every render.
    Derived, a mid-edit value like "600000." would be normalised away on the
    keystroke that produced it - the separator would vanish as you typed it.
  */
  const [display, setDisplay] = useState(() => groupMoney(value, spec));
  /** Digits before the caret, carried across the reformat. */
  const pendingCaret = useRef<number | null>(null);

  /*
    Re-sync when the value changes from OUTSIDE - a form reset, a hydrate, a
    currency switch that changes the precision. Compared as parsed values, so
    an echo of what the user just typed does not stomp their in-progress text.
  */
  useEffect(() => {
    if (parseMoney(display, spec) !== value) {
      setDisplay(groupMoney(value, spec));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, spec.group, spec.decimal, spec.maxFractionDigits, spec.locale]);

  // Restore the caret after React has painted the regrouped string, or it
  // lands at the end and typing inside a number throws you out of it.
  useLayoutEffect(() => {
    const target = pendingCaret.current;
    pendingCaret.current = null;
    if (target == null || !inputRef.current) return;
    const offset = caretAfterDigits(display, target);
    inputRef.current.setSelectionRange(offset, offset);
  }, [display]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const caret = event.target.selectionStart ?? raw.length;

    const plain = parseMoney(raw, spec);
    const grouped = groupMoney(plain, spec);

    /*
      Count the digits the user had passed, not the characters. Deleting the
      separator in "600,000" removes no digit, so the caret would sit still
      and the character reappear - stepping back one digit makes backspace
      over a separator delete the digit before it, which is what it looks
      like it should do.
    */
    let digits = digitsBefore(raw, caret);
    const deletedSeparator =
      raw.length < display.length && digitsBefore(raw, raw.length) === digitsBefore(display, display.length);
    if (deletedSeparator && digits > 0) digits -= 1;

    pendingCaret.current = digits;
    setDisplay(deletedSeparator ? groupMoney(dropDigit(plain, digits), spec) : grouped);
    onChange(deletedSeparator ? dropDigit(plain, digits) : plain);
  };

  // Tidy up whatever state typing left behind: a dangling separator, a
  // leading zero. The emitted value is re-derived so it cannot drift.
  const handleBlur = () => {
    const plain = parseMoney(display, spec);
    const tidy = plain === '' ? '' : String(plain).replace(/\.$/, '');
    setDisplay(groupMoney(tidy, spec));
    if (tidy !== value) onChange(tidy);
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      ref={inputRef}
      id={id}
      name={name}
      label={label}
      placeholder={placeholder ?? groupMoney('0', spec)}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      required={required}
      disabled={disabled}
      className={className}
      prefix={<span className="text-fg-muted">{currencySymbol(currency)}</span>}
    />
  );
};

/** Remove the digit at `index` from a plain numeric string. */
function dropDigit(plain: string, index: number): string {
  let seen = 0;
  for (let i = 0; i < plain.length; i += 1) {
    const char = plain[i];
    if (char >= '0' && char <= '9') {
      if (seen === index) return plain.slice(0, i) + plain.slice(i + 1);
      seen += 1;
    }
  }
  return plain;
}

export default CurrencyInput;
